import React, { useState } from 'react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import {
  Calendar,
  User,
  Flag,
  Tag,
  Pencil,
  Trash2,
  X,
  Save,
  Clock,
  Users,
  ListChecks
} from 'lucide-react';
import MemberSelector from './MemberSelector';
import SubtaskList from './SubtaskList';
import TimeProgress from './TimeProgress';

const statusConfig = {
  backlog: { label: 'Backlog', color: 'bg-slate-100 text-slate-700' },
  todo: { label: 'A Fazer', color: 'bg-blue-100 text-blue-700' },
  in_progress: { label: 'Em Progresso', color: 'bg-amber-100 text-amber-700' },
  review: { label: 'Em Revisão', color: 'bg-purple-100 text-purple-700' },
  done: { label: 'Concluído', color: 'bg-emerald-100 text-emerald-700' }
};

const priorityConfig = {
  low: { label: 'Baixa', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Média', color: 'bg-blue-100 text-blue-700' },
  high: { label: 'Alta', color: 'bg-amber-100 text-amber-700' },
  urgent: { label: 'Urgente', color: 'bg-red-100 text-red-700' }
};

const avatarColors = {
  blue: 'from-blue-500 to-cyan-500',
  purple: 'from-purple-500 to-pink-500',
  green: 'from-emerald-500 to-teal-500',
  orange: 'from-orange-500 to-amber-500',
  red: 'from-red-500 to-rose-500',
  indigo: 'from-indigo-500 to-blue-500'
};

export default function TaskModal({ task, open, onClose, onUpdate, onDelete, members = [] }) {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTask, setEditedTask] = useState(task);
  const [tagInput, setTagInput] = useState('');
  
  const assignedMembers = members.filter(m => task?.assignees?.includes(m.id));

  React.useEffect(() => {
    if (task) {
      setEditedTask(task);
      setIsEditing(false);
    }
  }, [task]);

  if (!task || !open) return null;

  const handleSave = () => {
    onUpdate(editedTask);
    setIsEditing(false);
  };

  const handleAddTag = (e) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      const newTags = [...(editedTask.tags || []), tagInput.trim()];
      setEditedTask({ ...editedTask, tags: newTags });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tagToRemove) => {
    const newTags = (editedTask.tags || []).filter(tag => tag !== tagToRemove);
    setEditedTask({ ...editedTask, tags: newTags });
  };

  const status = statusConfig[task.status] || statusConfig.backlog;
  const priority = priorityConfig[task.priority] || priorityConfig.medium;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 z-50"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-gradient-to-br from-white to-slate-50 rounded-xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 pb-4 border-b border-slate-100">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                {isEditing ? (
                  <input
                    type="text"
                    value={editedTask.title}
                    onChange={(e) => setEditedTask({ ...editedTask, title: e.target.value })}
                    className="w-full text-lg font-semibold border-2 border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                    placeholder="Título da tarefa"
                  />
                ) : (
                  <h2 className="text-xl font-semibold text-slate-800 pr-8">
                    {task.title}
                  </h2>
                )}
              </div>
            </div>
            
            {/* Status and Priority Badges */}
            <div className="flex items-center gap-2 mt-3">
              {isEditing ? (
                <>
                  <select
                    value={editedTask.status}
                    onChange={(e) => setEditedTask({ ...editedTask, status: e.target.value })}
                    className="h-8 px-3 border-2 border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    {Object.entries(statusConfig).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                  <select
                    value={editedTask.priority}
                    onChange={(e) => setEditedTask({ ...editedTask, priority: e.target.value })}
                    className="h-8 px-3 border-2 border-slate-200 rounded-lg focus:outline-none focus:border-blue-500"
                  >
                    {Object.entries(priorityConfig).map(([key, config]) => (
                      <option key={key} value={key}>{config.label}</option>
                    ))}
                  </select>
                </>
              ) : (
                <>
                  <span className={`${status.color} px-2 py-1 rounded text-xs font-semibold shadow-sm`}>{status.label}</span>
                  <span className={`${priority.color} px-2 py-1 rounded text-xs font-semibold shadow-sm inline-flex items-center`}>
                    <Flag className="w-3 h-3 mr-1" />
                    {priority.label}
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-5">
            {/* Description */}
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 block">
                Descrição
              </label>
              {isEditing ? (
                <textarea
                  value={editedTask.description || ''}
                  onChange={(e) => setEditedTask({ ...editedTask, description: e.target.value })}
                  placeholder="Adicione uma descrição..."
                  className="w-full min-h-[100px] border-2 border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500 resize-none"
                />
              ) : (
                <p className="text-slate-600 text-sm leading-relaxed">
                  {task.description || 'Sem descrição'}
                </p>
              )}
            </div>

            {/* Responsáveis */}
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Users className="w-3 h-3" />
                Responsáveis
              </label>
              {isEditing ? (
                <MemberSelector
                  members={members}
                  selectedIds={editedTask.assignees || []}
                  onChange={(ids) => setEditedTask({ ...editedTask, assignees: ids })}
                />
              ) : (
                <div>
                  {assignedMembers.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {assignedMembers.map((member) => {
                        const colorGradient = avatarColors[member.avatar_color] || avatarColors.blue;
                        return (
                          <div key={member.id} className="flex items-center gap-2 bg-slate-50 px-3 py-2 rounded-lg border border-slate-200">
                            <div className={`w-6 h-6 rounded-full bg-gradient-to-br ${colorGradient} flex items-center justify-center shadow-sm`}>
                              <span className="text-xs font-bold text-white">
                                {member.name.charAt(0).toUpperCase()}
                              </span>
                            </div>
                            <div>
                              <div className="text-sm font-medium text-slate-700">{member.name}</div>
                              <div className="text-xs text-slate-500">{member.profession}</div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <span className="text-sm text-slate-400">Nenhum responsável atribuído</span>
                  )}
                </div>
              )}
            </div>

            {/* Estimativa de Tempo */}
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Clock className="w-3 h-3" />
                Estimativa de Tempo (horas)
              </label>
              {isEditing ? (
                <input
                  type="number"
                  value={editedTask.estimated_hours || ''}
                  onChange={(e) => setEditedTask({ ...editedTask, estimated_hours: e.target.value ? parseFloat(e.target.value) : undefined })}
                  placeholder="Ex: 8"
                  min="0"
                  step="0.5"
                  className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              ) : (
                <span className="text-sm text-slate-700">
                  {task.estimated_hours ? `${task.estimated_hours}h` : 'Não definido'}
                </span>
              )}
            </div>

            {/* Time Progress */}
            {!isEditing && task.estimated_hours && task.started_at && (
              <div>
                <TimeProgress task={task} />
              </div>
            )}

            {/* Data Limite */}
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Calendar className="w-3 h-3" />
                Data Limite
              </label>
              {isEditing ? (
                <input
                  type="date"
                  value={editedTask.due_date || ''}
                  onChange={(e) => setEditedTask({ ...editedTask, due_date: e.target.value })}
                  className="w-full border-2 border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-500"
                />
              ) : (
                <span className="text-sm text-slate-700">
                  {task.due_date 
                    ? format(new Date(task.due_date), "dd 'de' MMMM, yyyy", { locale: ptBR })
                    : 'Sem data'}
                </span>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <Tag className="w-3 h-3" />
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {(isEditing ? editedTask.tags : task.tags)?.map((tag, i) => (
                  <span 
                    key={i} 
                    className="bg-gradient-to-r from-blue-100 to-cyan-100 text-blue-700 px-2 py-1 rounded text-xs font-semibold shadow-sm inline-flex items-center"
                  >
                    {tag}
                    {isEditing && (
                      <X 
                        className="w-3 h-3 ml-1 cursor-pointer hover:scale-125 transition-transform" 
                        onClick={() => handleRemoveTag(tag)}
                      />
                    )}
                  </span>
                ))}
                {isEditing && (
                  <input
                    type="text"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleAddTag}
                    placeholder="Adicionar tag..."
                    className="w-32 h-7 text-sm border-2 border-slate-200 rounded px-2 focus:outline-none focus:border-blue-500"
                  />
                )}
                {!isEditing && (!task.tags || task.tags.length === 0) && (
                  <span className="text-sm text-slate-400">Sem tags</span>
                )}
              </div>
            </div>

            {/* Subtasks */}
            <div>
              <label className="text-xs font-medium text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1">
                <ListChecks className="w-3 h-3" />
                Subtarefas
              </label>
              <SubtaskList
                subtasks={isEditing ? editedTask.subtasks : task.subtasks}
                onChange={(subtasks) => setEditedTask({ ...editedTask, subtasks })}
                isEditing={isEditing}
              />
            </div>

            {/* Metadata */}
            <div className="pt-4 border-t">
              <div className="flex items-center gap-4 text-xs text-slate-400">
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  Criado em {task.created_date && format(new Date(task.created_date), "dd/MM/yyyy 'às' HH:mm")}
                </span>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between p-6 pt-4 border-t">
            <button
              className="px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition-all shadow-sm hover:shadow-md font-semibold inline-flex items-center"
              onClick={() => onDelete(task)}
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Excluir
            </button>
            
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <button 
                    className="px-3 py-2 text-sm border-2 border-slate-200 rounded hover:bg-slate-50 transition-all"
                    onClick={() => setIsEditing(false)}
                  >
                    Cancelar
                  </button>
                  <button 
                    className="px-3 py-2 text-sm bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white rounded shadow-lg shadow-blue-200 hover:shadow-xl hover:scale-105 transition-all font-semibold inline-flex items-center"
                    onClick={handleSave}
                  >
                    <Save className="w-4 h-4 mr-1" />
                    Salvar
                  </button>
                </>
              ) : (
                <button 
                  className="px-3 py-2 text-sm border-2 border-slate-200 rounded hover:bg-slate-50 transition-all shadow-sm hover:shadow-md font-semibold inline-flex items-center"
                  onClick={() => setIsEditing(true)}
                >
                  <Pencil className="w-4 h-4 mr-1" />
                  Editar
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}